<template src="./Networking.html"></template>

<script>
import ArrobaMedellinSeeker from "@/components/ArrobaMedellinSeeker/ArrobaMedellinSeeker";
import ArrobaMedellinVerticalCardNetworking from "@/components/ArrobaMedellinVerticalCardNetworking/ArrobaMedellinVerticalCardNetworking";
import tutorial from "@/mixins/tutorial";
import categoryService from "@/services/Category";
import livekitService from "@/services/Livekit";
import networkingService from "@/services/Networking";
import networkingUsersService from "@/services/NetworkingUsers";
import profileService from "@/services/Profile";
import swiperLargeOption from "@/utils/swiper-large-option";
import { saveAs } from "file-saver";
import { SwiperSlide } from "vue-awesome-swiper";
import { required } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import ArrobaMedellinArrobitaNoData from "../../components/ArrobaMedellinArrobitaNoData/ArrobaMedellinArrobitaNoData";

export default {
  name: "Networking",
  mixins: [tutorial],
  components: {
    SwiperSlide,
    ArrobaMedellinVerticalCardNetworking,
    ArrobaMedellinSeeker,
    ArrobaMedellinArrobitaNoData,
  },
  data() {
    return {
      isComponentModalActive: false,
      errorMessage: false,
      room: {
        name: null,
        description: null,
        category: null,
        scheduledAt: null,
      },
      roomSelect: {},
      isActive: "Tus seguidores",
      swiperLargeOptionCreated: {
        ...swiperLargeOption,
        breakpoints: {
          360: {
            slidesPerView: 1,
            slidesPerGroup: 1,
            centeredSlides: true,
          },
          643: {
            slidesPerView: 1,
            slidesPerGroup: 1,
            centeredSlides: true,
          },
          1024: {
            slidesPerView: 2,
            slidesPerGroup: 2,
            centeredSlides: true,
          },
          1216: {
            slidesPerView: 2,
            slidesPerGroup: 2,
            centeredSlides: false,
          },
        },
      },
      swiperLargeOption: swiperLargeOption,
      typeCard: { button: "button--purple", size: "small" },
      categories: [],
      availableRoom: [],
      userInfo: {},
      itemsToShowSliderTop: Number(1),
      role: "",
      minScheduledAt: new Date(),
      // Id de la sala en edición; null = el modal crea una sala nueva
      editingRoomId: null,
      savingRoom: false,
    };
  },
  validations: {
    room: {
      name: { required },
      category: { required },
      description: { required },
      scheduledAt: { required },
    },
  },
  created() {
    this.getNetworkingInformation();
    this.role = this.$store.state.User.user.role.name;
  },
  mounted() {
    this.viewTutorial();
  },
  methods: {
    updateData() {
      (this.room.name = null),
        (this.room.description = null),
        (this.room.category = null),
        (this.room.scheduledAt = null);
    },

    /** El creador de la sala es su moderador */
    isRoomModerator(room) {
      return String(room.userId) === String(this.user?.id);
    },

    /** Ordena las salas de la más reciente a la más antigua (mayor id primero) */
    sortRoomsByRecency(rooms) {
      return [...rooms].sort((a, b) => b.id - a.id);
    },

    scheduleLabel(room) {
      if (!room.scheduledAt) return "";
      return this.$moment(room.scheduledAt).format("DD/MM/YYYY - hh:mm a");
    },
    async getNetworkingSearch(word) {
      try {
        if (!word) {
          this.availableRoom = this.sortRoomsByRecency(
            await networkingService.getNetworkings()
          );
          this.errorMessage = false;
        } else {
          this.availableRoom = this.sortRoomsByRecency(
            await networkingService.searchNetworking(word)
          );
          if (this.availableRoom.length === 0) this.errorMessage = true;
        }
      } catch (e) {
        console.error("error", e);
      }
    },

    async getNetworkingInformation() {
      try {
        this.availableRoom = this.sortRoomsByRecency(
          await networkingService.getNetworkings()
        );
        this.userInfo = await profileService.getUserData();
        this.categories = await categoryService.getCategories();
      } catch (e) {
        this.$buefy.toast.open({
          duration: 5000,
          message: e.response?.data?.message ?? "Se presentó un error",
          position: "is-bottom",
          type: "is-danger",
        });
        console.error("Error: Networking/getNetworkingInformation()", e);
      }
    },

    async saveNetworkingUsers(idNetworking) {
      return await networkingUsersService.createdNetworkingUsers({
        networkingId: idNetworking,
      });
    },

    /** Moderador de la sala o administrador */
    canManageRoom(room) {
      return this.isRoomModerator(room) || this.role === "Administrator";
    },

    openCreateRoom() {
      this.resetRoomForm();
      this.isComponentModalActive = true;
    },

    openEditRoom(room) {
      this.editingRoomId = room.id;
      this.room.name = room.name;
      this.room.description = room.description;
      this.room.category = room.categoryId;
      this.room.scheduledAt = room.scheduledAt ? new Date(room.scheduledAt) : null;
      this.isComponentModalActive = true;
    },

    resetRoomForm() {
      this.editingRoomId = null;
      this.updateData();
    },

    async updateRoom() {
      if (this.savingRoom) return;
      this.savingRoom = true;
      try {
        await networkingService.changeNetworkings({
          id: this.editingRoomId,
          category: Number(this.room.category),
          name: this.room.name,
          description: this.room.description,
          scheduledAt: new Date(this.room.scheduledAt).toISOString(),
        });
        this.$buefy.toast.open({
          duration: 4000,
          position: "is-bottom",
          message: "La sala se actualizó con éxito",
          type: "is-success",
        });
        this.isComponentModalActive = false;
        this.resetRoomForm();
        await this.getNetworkingSearch("");
      } catch (e) {
        console.error("Error: Networking/updateRoom()", e);
        this.$buefy.toast.open({
          duration: 5000,
          position: "is-bottom",
          message: e.response?.data?.message ?? "No fue posible actualizar la sala",
          type: "is-danger",
        });
      } finally {
        this.savingRoom = false;
      }
    },

    /** Reporte de todos los que ingresaron a la sala, en CSV para Excel */
    async downloadReport(room) {
      try {
        const report = await networkingService.getNetworkingReport(room.id);
        const formatDate = (value) =>
          value ? this.$moment(value).format("DD/MM/YYYY hh:mm a") : "";
        const roleLabels = {
          Student: "Estudiante",
          Teacher: "Docente",
          TeacherInvestigator: "Docente investigador",
          Entrepreneur: "Empresario",
          Administrator: "Administrador",
          University: "Universidad",
          AdministratorCall: "Administrador de convocatorias",
          Invited: "Invitado",
        };
        const header = [
          "Nombre",
          "Apellido",
          "Correo",
          "Tipo de documento",
          "Documento",
          "Rol",
          "Papel en la sala",
          "Primer ingreso",
          "Último ingreso",
        ];
        const rows = report.users.map((u) => [
          u.name,
          u.lastName,
          u.email,
          u.documentType,
          u.document,
          roleLabels[u.role] || u.role,
          u.isModerator ? "Moderador" : "Participante",
          formatDate(u.firstEntryAt),
          formatDate(u.lastEntryAt),
        ]);
        const info = report.networking;
        const preamble = [
          ["Sala", info.name],
          ["Categoría", info.categoryName],
          ["Moderador", info.moderatorName],
          ["Fecha programada", formatDate(info.scheduledAt)],
          ["Total de personas que ingresaron", report.total],
          [],
        ];
        // `;` y BOM: Excel en español abre el archivo con columnas y tildes bien
        const escape = (value) => {
          let text = value === null || value === undefined ? "" : String(value);
          // Evita que Excel interprete un nombre como fórmula
          if (/^[=+\-@]/.test(text)) text = `'${text}`;
          return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
        };
        const csv = [...preamble, header, ...rows]
          .map((line) => line.map(escape).join(";"))
          .join("\r\n");
        const safeName = String(info.name || "sala")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-zA-Z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        saveAs(
          new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }),
          `reporte-networking-${safeName}-${this.$moment().format("YYYYMMDD-HHmm")}.csv`
        );
      } catch (e) {
        console.error("Error: Networking/downloadReport()", e);
        this.$buefy.toast.open({
          duration: 5000,
          position: "is-bottom",
          message: e.response?.data?.message ?? "No fue posible generar el reporte",
          type: "is-danger",
        });
      }
    },

    /**
     * Cupo en vivo antes de registrar el ingreso: así quien no cabe no queda
     * en el reporte como si hubiera entrado. Si la consulta falla se deja
     * seguir: gestor-api vuelve a validar al emitir el token.
     */
    async roomHasSpace(room) {
      if (this.isRoomModerator(room)) return true;
      try {
        const occupancy = await livekitService.networkingOccupancy(room.meetUrl);
        if (occupancy.isFull && !occupancy.alreadyInside) {
          this.$buefy.dialog.alert({
            title: "Sala llena",
            message: `<b>${room.name}</b> alcanzó su aforo de ${occupancy.capacity} personas (${occupancy.count} conectadas). Intenta más tarde o elige otra sala.`,
            type: "is-warning",
            hasIcon: true,
            icon: "exclamation-triangle",
            iconPack: "fas",
            confirmText: "Entendido",
          });
          return false;
        }
      } catch (e) {
        console.warn("No se pudo consultar el cupo de la sala", e);
      }
      return true;
    },

    async verticalCardClickButton(button) {
      const idNetworking = button.room.id;
      if (!(await this.roomHasSpace(button.room))) return;
      const userNetworking = await this.saveNetworkingUsers(idNetworking);
      if (userNetworking) {
        this.$router.push({
          name: "Meet",
          params: {
            idNetworking: idNetworking,
            isPrivateRoom: false,
            isUrl: false,
          },
        });
      }
    },

    async createdNetworking() {
      try {
        const networkingCreate = await networkingService.createNetworking({
          ...this.room,
          scheduledAt: new Date(this.room.scheduledAt).toISOString(),
        });
        const userNetworking = await this.saveNetworkingUsers(
          networkingCreate.id
        );
        if (userNetworking) {
          this.$buefy.toast.open({
            duration: 5000,
            position: "is-bottom",
            message: "La sala se ha creado con éxito!",
            type: "is-success",
          });
          this.updateData();
          this.$router.push({
            name: "Meet",
            params: {
              idNetworking: networkingCreate.id,
              isPrivateRoom: false,
              isUrl: false,
            },
          });
        }
      } catch (e) {
        this.isComponentModalActive = false;
        this.$buefy.toast.open({
          duration: 5000,
          position: "is-bottom",
          message: e.response.data.message ?? "Se presentó un error",
          type: "is-danger",
        });
        this.updateData();
        console.error(e);
      }
    },
    viewTutorial() {
      // El único paso del tour apunta al botón "Crear una nueva sala", que
      // los estudiantes ya no ven.
      if (!this.canCreateRoom) return;
      this.dataTutorial("networking");
    },
  },
  computed: {
    ...mapGetters({
      user: "getUser",
    }),
    // Los estudiantes no pueden crear salas de networking (mismo scope que
    // el POST /api/networking/create del backend)
    canCreateRoom() {
      return ["Teacher", "TeacherInvestigator", "Entrepreneur", "Administrator"].some(
        (a) => a === this.role
      );
    },
    roleValidationNoAccess() {
      return ["University", "AdministratorCall"].some(
        (a) => a === this.role
      );
    },
    roleValidationInvited() {
      return ["Invited"].some((a) => a === this.role);
    },
  },
  user: function (value) {
    this.userInfo = value;
  },
};
</script>

<style lang="scss" src="./Networking.scss" scoped></style>
